import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Info } from 'lucide-react'
import type { VendorAdvisory, VendorName } from '@/lib/types'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'

/**
 * V5 Slice 3 — admin-only moderation surface for vendor advisories.
 *
 * Fetches the *full* per-CVE list (including suppressed rows) from
 * `GET /api/admin/vendor-advisories/by-cve/:cveId` so each row can be toggled
 * Suppress / Unsuppress. Mutations invalidate both the admin list query and
 * the public `['vulnerabilities', :id]` query so the CISO `RemediationSection`
 * reflects moderator decisions on the next render.
 */

const VENDOR_LABEL: Record<VendorName, string> = {
  msrc: 'Microsoft',
  redhat: 'Red Hat',
  ubuntu: 'Ubuntu',
  cisco: 'Cisco',
  paloalto: 'Palo Alto',
  suse: 'SUSE',
}

export interface VendorAdvisoryAdminSectionProps {
  cveId: string
  /** Admin vuln row UUID — used to invalidate the public `useVulnerability` cache. */
  vulnId?: string
}

export function VendorAdvisoryAdminSection({
  cveId,
  vulnId,
}: VendorAdvisoryAdminSectionProps) {
  const qc = useQueryClient()

  const listQuery = useQuery({
    queryKey: ['admin-vendor-advisories', cveId],
    queryFn: () =>
      api.get<VendorAdvisory[]>(
        `/admin/vendor-advisories/by-cve/${encodeURIComponent(cveId)}`,
      ),
    enabled: Boolean(cveId),
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin-vendor-advisories', cveId] })
    if (vulnId) {
      qc.invalidateQueries({ queryKey: ['vulnerabilities', vulnId] })
    }
  }

  const suppress = useMutation({
    mutationFn: (id: string) =>
      api.post(`/admin/vendor-advisories/${id}/suppress`, {}),
    onSuccess: invalidate,
  })

  const unsuppress = useMutation({
    mutationFn: (id: string) =>
      api.post(`/admin/vendor-advisories/${id}/unsuppress`, {}),
    onSuccess: invalidate,
  })

  if (listQuery.isLoading) {
    return (
      <section
        data-testid="vendor-advisory-admin-section"
        className="rounded-md border border-border p-4"
      >
        <h3 className="text-sm font-semibold">Vendor advisories</h3>
        <p className="mt-2 text-xs text-muted-foreground">Loading…</p>
      </section>
    )
  }

  if (listQuery.isError) {
    return (
      <section
        data-testid="vendor-advisory-admin-section"
        className="rounded-md border border-border p-4"
      >
        <h3 className="text-sm font-semibold">Vendor advisories</h3>
        <p className="mt-2 text-xs text-destructive">
          Failed to load vendor advisories.
        </p>
      </section>
    )
  }

  const rows = listQuery.data ?? []

  return (
    <section
      data-testid="vendor-advisory-admin-section"
      className="rounded-md border border-border p-4"
    >
      <h3 className="text-sm font-semibold">Vendor advisories</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Hide a vendor advisory from CISO view when it is duplicate, wrong, or
        superseded. Unsuppress to restore.
      </p>
      {rows.length === 0 ? (
        <p className="mt-3 text-xs text-muted-foreground">
          No vendor advisories for this CVE.
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {rows.map((a) => {
            const isSuppressed = a.suppressedAt != null
            return (
              <li
                key={a.id}
                data-testid="admin-advisory-row"
                className="flex flex-wrap items-center justify-between gap-2 text-sm"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-medium text-foreground">
                      {VENDOR_LABEL[a.vendor]}
                    </span>
                    <a
                      href={a.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate font-mono text-xs text-primary hover:underline"
                    >
                      {a.advisoryId}
                    </a>
                    {isSuppressed && (
                      <span
                        data-testid="admin-advisory-suppressed-badge"
                        className="rounded bg-destructive/10 px-1.5 py-0.5 text-xs font-medium text-destructive"
                      >
                        suppressed
                      </span>
                    )}
                  </div>
                  {a.fixVersion && (
                    <div className="font-mono text-xs text-muted-foreground">
                      {a.fixVersion}
                    </div>
                  )}
                  {isSuppressed && a.suppressedBy && (
                    <div className="text-xs text-muted-foreground">
                      Suppressed by{' '}
                      <span className="font-mono">{a.suppressedBy}</span>
                    </div>
                  )}
                </div>
                {isSuppressed ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => unsuppress.mutate(a.id)}
                    disabled={unsuppress.isPending}
                    aria-label={`Unsuppress ${a.advisoryId}`}
                  >
                    Unsuppress
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => suppress.mutate(a.id)}
                    disabled={suppress.isPending}
                    aria-label={`Suppress ${a.advisoryId}`}
                  >
                    Suppress
                  </Button>
                )}
              </li>
            )
          })}
        </ul>
      )}
      <p
        data-testid="admin-advisory-pending-note"
        className="mt-3 flex items-start gap-1.5 text-xs text-muted-foreground"
      >
        <Info aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Cisco PSIRT and Palo Alto Networks advisories — pending vendor API
          access.
        </span>
      </p>
    </section>
  )
}
